import { GLOBAL_FIELDS } from '@datalas/common';
import compact from 'lodash/compact';

import api from '../../lib/api';

export default {
  namespaced: true,

  state: {
    // These are the things that get passed in to generate the query params:
    indicatorId: null,
    breakdown: {},
    primaryGeoCode: null,
    secondaryGeoCode: null,
    geoType: null,
    dataVersion: null,
    datasetName: null,
    displayRev: null,
    args: {},

    // Internal state:
    requestId: 0,
  },

  getters: {
    breakdownSlug: ({ breakdown }) => breakdown.slug ?? '',

    isMap: ({ breakdown }) => breakdown.config.subtype === 'map',

    isRank: ({ breakdown }) => breakdown.config.subtype === 'rank',

    isGeoDisplay: (_, { isMap, isRank }) => isMap || isRank,

    valueFields: ({ breakdown: { selectedFields } }) => {
      const fields = selectedFields && selectedFields.concat(GLOBAL_FIELDS);
      // The server can't handle too many params, if there are more than 50
      // fields don't bother
      return fields && fields.length < 50 ? fields : undefined;
    },

    geoTypes: ({ breakdown, geoType, primaryGeoCode }, { isMap, isRank }) => {
      if (isRank) {
        const computedGeoType = primaryGeoCode
          ? [primaryGeoCode.substring(0, 2)]
          : undefined;
        return computedGeoType;
      }

      const geoTypes = breakdown.config.geoTypes || [];

      if (isMap) return [geoType || geoTypes[0] || '04'];

      return geoTypes.length ? geoTypes : undefined;
    },

    geoType: ({ geoType }, { geoTypes }) =>
      geoType || (geoTypes && geoTypes[0]),

    primaryGeoCode: ({ primaryGeoCode, breakdown }) => {
      return primaryGeoCode || (breakdown.config.geoCodes || [])[0];
    },

    secondaryGeoCode: ({ secondaryGeoCode, breakdown }) => {
      // If geo compare is not allowed, ignore secondaryGeoCode
      if (!breakdown.config?.allowGeoCompare) return undefined;
      // If we're passed a geoCompare code, use that
      if (secondaryGeoCode) return secondaryGeoCode;
      // If secondaryGeoCode is undefined try to find a default
      if (secondaryGeoCode === undefined)
        return (breakdown.config.geoCodes || [])[1];
      // If secondaryGeoCode is defined but falsey, don't do a geo compare
      return undefined;
    },

    geoCodes: (_, { primaryGeoCode, secondaryGeoCode }) =>
      compact([primaryGeoCode, secondaryGeoCode]),

    queryGeoCodes: (_, { geoCodes, geoTypes }) => {
      // When we have geo types, don't use geo codes
      if (geoTypes) return undefined;
      // Otherwise just use the primary and (optional) secondary geo codes
      return geoCodes;
    },

    // When there's only 1 geo code don't send an array. This makes it work for
    // both 'geo_code' and 'selected_geo_code', the later of which doesn't take
    // an array
    geoCodesValue: (_, { queryGeoCodes }) => {
      if (!queryGeoCodes) return undefined;
      if (queryGeoCodes.length === 1) return queryGeoCodes[0];
      return queryGeoCodes;
    },

    additionalGeoCodes: ({ breakdown }, { geoCodes, queryGeoCodes }) =>
      geoCodes
        .concat(breakdown.geoCompareOptions || [])
        .filter((code) => !queryGeoCodes || !queryGeoCodes.includes(code)),

    geoGroup: ({ breakdown }, { queryGeoCodes }) => {
      if (breakdown.geoGroup) return breakdown.geoGroup;
      if (queryGeoCodes) return undefined;
      // BAEA-specific hack:
      return (breakdown.config.queryFilters.geo_code_long || [])[0];
    },

    // Only paginate geo displays (rank, map) or if queryLimit is set in config
    paginate: ({ breakdown }, { isGeoDisplay }) =>
      Boolean(breakdown.queryLimit) || isGeoDisplay,

    limit: ({ breakdown }, { isGeoDisplay, geoCodes }) => {
      if (breakdown.queryLimit) return breakdown.queryLimit;
      if (isGeoDisplay) return 1000000000;

      // If a limit is not set we will attempt to estimate one based on the
      // number of combinations of filters * the number of geoCodes.
      // This only works if we know we're querying data based on a finite number
      // of geoCodes; it can't be calculated for displays based on geoTypes
      // (i.e. rank charts and maps).
      const queryCounts = (breakdown.queryFilters || []).reduce(
        (acc, filter) => {
          const [type] = filter.split(':');
          return { ...acc, [type]: (acc[type] || 0) + 1 };
        },
        {},
      );
      const geoCount = breakdown.allowGeoCompare
        ? 2
        : (geoCodes || []).length || 1;

      return (
        Object.keys(queryCounts).reduce(
          (acc, type) => (acc || 1) * queryCounts[type],
          0,
        ) * geoCount
      );
    },

    params: ({ breakdown, indicatorId, dataVersion, args }, getters) => ({
      indicator_id: indicatorId,
      display_id: breakdown.slug,
      filter: breakdown.queryFilters,
      geo_type: getters.geoTypes,
      geo_group: getters.geoGroup,
      geo_code: getters.geoCodesValue,
      value_fields: getters.valueFields,
      paginate: getters.paginate,
      data_version: dataVersion,

      // Params from custom url param args
      environment: args.environment,
    }),
  },

  actions: {
    fetch: async ({ commit, state, getters }, payload) => {
      commit('setState', payload);

      const { datasetName, displayRev } = state;
      const { params, additionalGeoCodes, isRank, isMap } = getters;

      return api.fetchData(
        { ...params, datasetName, displayRev },
        !isRank && !isMap ? additionalGeoCodes : [],
      );
    },
  },

  /* eslint-disable no-param-reassign */
  mutations: {
    setState: (state, payload) => {
      Object.assign(state, payload);
    },
  },
};
